import PropTypes from 'prop-types';

import NoSSR from 'react-no-ssr';

import { trans } from '@spotahome/soyuz/client';
import { AvailabilityCalendar } from '@spotahome/ui-library';

import ListRow from '@spotahome/marketplace-common/src/components/ListRow';

import PricingShape from '@spotahome/marketplace-common/src/shapes/pricing';

import { getHumanReadableAvailability } from '@spotahome/marketplace-common/src/utils/getHumanReadableAvailability';
import {
  getHumanReadableMinStay,
  getHumanReadableMaxStay
} from '@spotahome/marketplace-common/src/utils/getHumanReadableStay';
import { getLatestAvailability } from '@spotahome/marketplace-common/src/utils/getLatestAvailability';

import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import { ListingSectionObservable } from '../ListingSection';

import './ListingAvailabilities.scss';

const ListingAvailabilities = ({
  availableFrom,
  dateUpdated,
  minStay = 0,
  maxStay = 0,
  bookingTooEarlyDays = 0,
  unavailabilities = [],
  seasonalPrices = null,
  currency
}) => (
  <ListingSectionObservable section="availabilities">
    <div className="listing-availabilities" data-test="listing-availabilities">
      <ListingTitle role="secondary">
        {trans('listing.availabilities.title')}
      </ListingTitle>

      <div className="listing-availabilities-structured">
        <div className="listing-availabilities-structured__dates">
          <ListRow
            renderLeftBlock={() => (
              <p className="listing-availabilities-structured__row">
                {trans('listing.availabilities.calendar.available-from')}
              </p>
            )}
            renderRightBlock={() => (
              <div className="listing-availabilities-structured__row--bold">
                {getHumanReadableAvailability(availableFrom, false)}
              </div>
            )}
          />
          <ListRow
            renderLeftBlock={() => (
              <p className="listing-availabilities-structured__row">
                {trans('listing.availabilities.calendar.min-stay')}
              </p>
            )}
            renderRightBlock={() => (
              <div
                className="listing-availabilities-structured__row--bold"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: getHumanReadableMinStay(minStay)
                }}
              />
            )}
          />
          <ListRow
            renderLeftBlock={() => (
              <p className="listing-availabilities-structured__row">
                {trans('listing.availabilities.calendar.max-stay')}
              </p>
            )}
            renderRightBlock={() => (
              <div
                className="listing-availabilities-structured__row--bold"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: getHumanReadableMaxStay(maxStay)
                }}
              />
            )}
          />
          <ListRow
            withBorder={bookingTooEarlyDays > 0}
            renderLeftBlock={() => (
              <p className="listing-availabilities-structured__row">
                {trans('listing.availabilities.calendar.last-updated')}
              </p>
            )}
            renderRightBlock={() => (
              <div className="listing-availabilities-structured__row--bold listing-availabilities-structured__row--aligned-right">
                {getLatestAvailability(dateUpdated)}
              </div>
            )}
          />
          {bookingTooEarlyDays > 0 && (
            <ListRow
              withBorder={false}
              renderLeftBlock={() => (
                <p className="listing-availabilities-structured__row">
                  {trans('listing.availabilities.calendar.booking-window')}
                </p>
              )}
              renderRightBlock={() => (
                <div className="listing-availabilities-structured__row--bold listing-availabilities-structured__row--aligned-right">
                  {trans(
                    'listing.availabilities.calendar.booking-window.days',
                    {
                      days: bookingTooEarlyDays
                    }
                  )}
                </div>
              )}
            />
          )}
        </div>
        <div className="listing-availabilities-structured__calendar">
          <NoSSR>
            <AvailabilityCalendar
              unavailabilities={unavailabilities}
              seasonalPrices={seasonalPrices}
              currency={currency}
            />
          </NoSSR>
        </div>
      </div>
    </div>
  </ListingSectionObservable>
);

ListingAvailabilities.propTypes = {
  availableFrom: PropTypes.string.isRequired,
  dateUpdated: PropTypes.string.isRequired,
  seasonalPrices: PropTypes.shape(PricingShape.monthlyPrice.pricesByMonth),
  unavailabilities: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string
    })
  ),
  maxStay: PropTypes.number,
  minStay: PropTypes.number,
  bookingTooEarlyDays: PropTypes.number,
  currency: PropTypes.string.isRequired
};

export default ListingAvailabilities;
